import { useState, useCallback } from 'react';
import { blogPosts as defaultPosts } from '@/mocks/blog';
import type { BlogPost } from '@/mocks/blog';

const STORAGE_KEY = 'blog_posts';

function loadPosts(): BlogPost[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // corrupted data, fall through
  }
  const seeded = [...defaultPosts];
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
  } catch {
    // quota exceeded or unavailable
  }
  return seeded;
}

let cachedPosts: BlogPost[] | null = null;

export function getBlogPosts(): BlogPost[] {
  if (!cachedPosts) cachedPosts = loadPosts();
  return cachedPosts;
}

export function saveBlogPosts(posts: BlogPost[]): void {
  cachedPosts = posts;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(posts));
  } catch {
    // silently fail if localStorage is full
  }
}

export function useBlogPosts() {
  const [posts, setPosts] = useState<BlogPost[]>(() => loadPosts());

  const refresh = useCallback(() => {
    const fresh = loadPosts();
    setPosts(fresh);
  }, []);

  const savePost = useCallback((post: BlogPost) => {
    const current = loadPosts();
    const idx = current.findIndex((p) => p.slug === post.slug);
    if (idx >= 0) {
      current[idx] = post;
    } else {
      current.unshift(post);
    }
    saveBlogPosts(current);
    setPosts([...current]);
  }, []);

  const deletePost = useCallback((slug: string) => {
    const current = loadPosts().filter((p) => p.slug !== slug);
    saveBlogPosts(current);
    setPosts([...current]);
  }, []);

  return { posts, savePost, deletePost, refresh };
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/-+/g, '-')
    .trim()
    .replace(/^-+|-+$/g, '');
}